import { useState, useEffect } from "react";
import axios, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, VideoIcon } from "lucide-react";
import { MAX_FILE_SIZE } from "@/lib/constants";
import UploadProgress from "./UploadProgress";
import EditableTitle from "./EditableTitle";
import UploadedVideoScheduler from "./UploadedVideoScheduler";

const VideoUploadPanel = ({ user }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [fetchingVideos, setFetchingVideos] = useState(true);

  useEffect(() => { fetchUploadedVideos(); }, []);

  const fetchUploadedVideos = async () => {
    try {
      const response = await axios.get(`${API}/uploads`, {
        headers: { Authorization: `Bearer ${user.access_token}` }
      });
      setUploadedVideos(response.data.videos || []);
    } catch (error) {
      console.error('Failed to fetch uploaded videos:', error);
      toast.error('Failed to load uploaded videos');
    } finally {
      setFetchingVideos(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 2GB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(file.size);
    const startTime = Date.now();
    setUploadStartTime(startTime);

    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await axios.post(`${API}/uploads`, formData, {
        headers: {
          Authorization: `Bearer ${user.access_token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const elapsed = (Date.now() - startTime) / 1000;
          const speed = progressEvent.loaded / elapsed;

          setUploadProgress(percentCompleted);
          setUploadedBytes(progressEvent.loaded);
          setUploadSpeed(speed);
        }
      });

      toast.success(`Video uploaded successfully! ${response.data.size_mb}MB`);
      fetchUploadedVideos();
      event.target.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadedBytes(0);
      setTotalBytes(0);
      setUploadSpeed(0);
    }
  };

  const handleDeleteVideo = async (videoId, filename) => {
    if (!confirm(`Delete "${filename}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/uploads/${videoId}`, {
        headers: { Authorization: `Bearer ${user.access_token}` }
      });
      toast.success('Video deleted successfully');
      fetchUploadedVideos();
    } catch (error) {
      console.error('Failed to delete video:', error);
      toast.error('Failed to delete video');
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        {!uploading ? (
          <div className="text-center">
            <VideoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="video-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">Upload your video files</span>
                <span className="mt-1 block text-sm text-gray-600">MP4, AVI, MOV, MKV, WMV up to 2GB</span>
              </label>
              <input id="video-upload" type="file" className="hidden" accept=".mp4,.avi,.mov,.mkv,.wmv" onChange={handleFileUpload} disabled={uploading} />
            </div>
            <Button className="mt-4" onClick={() => document.getElementById('video-upload').click()} disabled={uploading}>
              Choose Video File
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <VideoIcon className="mx-auto h-12 w-12 text-red-500 animate-pulse" />
              <h3 className="mt-2 text-lg font-semibold text-gray-900">Uploading Video</h3>
              <p className="text-sm text-gray-600">Please wait while your video is being uploaded...</p>
            </div>
            <UploadProgress progress={uploadProgress} uploadedBytes={uploadedBytes} totalBytes={totalBytes} speed={uploadSpeed} />
            <div className="text-center">
              <Button variant="outline" size="sm" onClick={() => {
                if (confirm('Are you sure you want to cancel the upload? You will lose progress.')) {
                  window.location.reload();
                }
              }} className="text-gray-600">
                Cancel Upload
              </Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Uploaded Videos</h3>
        {fetchingVideos ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading videos...</p>
          </div>
        ) : uploadedVideos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <VideoIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No videos uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {uploadedVideos.map((video) => (
              <Card key={video.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <EditableTitle video={video} user={user} onUpdate={() => fetchUploadedVideos()} />
                    <p className="text-sm text-gray-600 mt-1">
                      {video.size_mb || 0}MB | Uploaded {video.uploaded_at ? new Date(video.uploaded_at).toLocaleDateString() : 'Unknown date'}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">Schedule</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Schedule: {video.custom_title || video.original_filename}</DialogTitle>
                        </DialogHeader>
                        <UploadedVideoScheduler video={video} user={user} />
                      </DialogContent>
                    </Dialog>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteVideo(video.id, video.title || video.filename || 'this video')} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUploadPanel;
